import type {
  ReportedMonitorListItem,
  ReportedMonitorSummary,
  ReportedRunPage,
} from '@simplemon/lib';
import { readPublicError } from './error';

export class JobNotFoundError extends Error {}

async function readJobsResponse<T>(response: Response, fallback: string): Promise<T> {
  if (response.status === 404) throw new JobNotFoundError('Job not found');
  if (!response.ok) throw new Error(await readPublicError(response, fallback));
  try {
    return (await response.json()) as T;
  } catch {
    throw new Error(fallback);
  }
}

export const jobsApi = {
  async list(): Promise<ReportedMonitorListItem[]> {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const params = new URLSearchParams();
    if (timeZone) params.set('timeZone', timeZone);
    const query = params.toString();
    const response = await fetch(`/api/reported-monitors${query ? `?${query}` : ''}`, {
      credentials: 'include',
    });
    return readJobsResponse(response, 'Could not load jobs');
  },

  async get(id: number): Promise<ReportedMonitorSummary> {
    const response = await fetch(`/api/reported-monitors/${id}`, { credentials: 'include' });
    return readJobsResponse(response, 'Could not load job');
  },

  async runs(id: number, cursor?: string): Promise<ReportedRunPage> {
    const params = new URLSearchParams({ limit: '50' });
    if (cursor) params.set('cursor', cursor);
    const response = await fetch(`/api/reported-monitors/${id}/runs?${params}`, {
      credentials: 'include',
    });
    return readJobsResponse(response, 'Could not load job runs');
  },
};
